import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Video,
  Upload,
  Mail,
  Calendar,
  Clock,
  Loader2,
  ExternalLink,
  CheckCircle2,
  AlertCircle,
  Brain,
  FileText,
  ListTodo,
  Users,
  RefreshCw,
  Trash2,
  Eye,
  Play,
  Mic,
  MicOff,
  Square,
  Send,
  FileSignature,
  CalendarPlus,
  ThumbsUp,
  Sparkles,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icones pour les plateformes
const PlatformIcon = ({ platform }) => {
  const colors = {
    zoom: 'bg-blue-100 text-blue-600',
    meet: 'bg-green-100 text-green-600',
    teams: 'bg-purple-100 text-purple-600',
    webex: 'bg-orange-100 text-orange-600',
    autre: 'bg-gray-100 text-gray-600'
  };
  
  return (
    <div className={`p-2 rounded-full ${colors[platform] || colors.autre}`}>
      <Video className="w-5 h-5" />
    </div>
  );
};

export default function AssistantVisioPage() {
  const { token } = useAuth();
  const { activeSociete } = useWorkspace();
  const [activeTab, setActiveTab] = useState('invitations');
  
  // États invitations
  const [invitations, setInvitations] = useState([]);
  const [loadingInvitations, setLoadingInvitations] = useState(true);
  const [googleConnected, setGoogleConnected] = useState(true);
  
  // États enregistrements
  const [records, setRecords] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(true);
  
  // États upload
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadTitle, setUploadTitle] = useState('');
  
  // État détail
  const [selectedRecord, setSelectedRecord] = useState(null);

  // === STREAMING RECORDING ===
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [streamRecordId, setStreamRecordId] = useState(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const chunkIntervalRef = useRef(null);

  // === POST-VISIO ACTIONS ===
  const [showActionsDialog, setShowActionsDialog] = useState(false);
  const [actionRecord, setActionRecord] = useState(null);
  const [editEmail, setEditEmail] = useState({ destinataire: '', objet: '', corps: '' });
  const [editMeet, setEditMeet] = useState({ date: '', heure: '14:00', objet: '', invites: '', duree_minutes: 30 });
  const [sendingAction, setSendingAction] = useState(null);

  // === COACH CLOSING IA ===
  const [coachingTips, setCoachingTips] = useState([]);
  const [liveTranscript, setLiveTranscript] = useState('');
  const speechRecRef = useRef(null);
  const coachingIntervalRef = useRef(null);
  const transcriptBufferRef = useRef('');

  const headers = { Authorization: `Bearer ${token}`, 'X-Societe-Id': activeSociete?.id || '' };
  const jsonHeaders = { ...headers, 'Content-Type': 'application/json' };

  // === FONCTIONS STREAMING ===
  const startRecording = async () => {
    try {
      // Demander accès au micro
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Démarrer la session côté backend
      const res = await fetch(`${API_URL}/api/visio/stream/start`, {
        method: 'POST', headers: jsonHeaders
      });
      const data = await res.json();
      if (!data.success) throw new Error('Erreur démarrage session');
      
      setStreamRecordId(data.record_id);

      // Créer le MediaRecorder
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      mediaRecorderRef.current = mediaRecorder;

      // Envoyer les chunks toutes les 5 secondes
      mediaRecorder.ondataavailable = async (e) => {
        if (e.data.size > 0 && data.record_id) {
          try {
            await fetch(`${API_URL}/api/visio/stream/${data.record_id}/chunk`, {
              method: 'POST',
              headers: { ...headers, 'Content-Type': 'application/octet-stream' },
              body: e.data
            });
          } catch (err) {
            console.error('Erreur envoi chunk:', err);
          }
        }
      };

      mediaRecorder.start(5000); // chunk toutes les 5s
      setIsRecording(true);
      setRecordingTime(0);
      setCoachingTips([]);
      setLiveTranscript('');
      transcriptBufferRef.current = '';

      // Timer affichage
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

      // Démarrer la transcription live (Web Speech API pour le coaching)
      startLiveTranscription(data.record_id);

      toast.success('Enregistrement + Coach IA démarrés');
    } catch (err) {
      console.error('Erreur micro:', err);
      toast.error('Impossible d\'accéder au microphone');
    }
  };

  const startLiveTranscription = (recordId) => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SR();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'fr-FR';
      
      recognition.onresult = (event) => {
        let final = '';
        for (let i = 0; i < event.results.length; i++) {
          if (event.results[i].isFinal) final += event.results[i][0].transcript + ' ';
        }
        if (final) {
          transcriptBufferRef.current += final;
          setLiveTranscript(transcriptBufferRef.current.slice(-1000));
        }
      };
      
      recognition.onerror = (e) => {
        if (e.error !== 'no-speech' && e.error !== 'aborted') {
          console.warn('Speech error:', e.error);
        }
      };
      
      recognition.onend = () => {
        // Relancer si toujours en enregistrement
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
          try { recognition.start(); } catch {}
        }
      };
      
      try { recognition.start(); } catch {}
      speechRecRef.current = recognition;
    }
    
    // Demander du coaching toutes les 30 secondes
    coachingIntervalRef.current = setInterval(async () => {
      if (!transcriptBufferRef.current) return;
      try {
        const res = await fetch(`${API_URL}/api/visio/stream/${recordId}/coaching`, {
          method: 'POST',
          headers: jsonHeaders,
          body: JSON.stringify({ accumulated_transcript: transcriptBufferRef.current })
        });
        const data = await res.json();
        if (data.tips?.length > 0) {
          setCoachingTips(prev => [...data.tips.map(t => ({ ...t, id: Date.now() + Math.random(), time: new Date() })), ...prev].slice(0, 20));
        }
      } catch {}
    }, 30000);
  };

  const stopRecording = async () => {
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }
      if (timerRef.current) clearInterval(timerRef.current);
      if (coachingIntervalRef.current) clearInterval(coachingIntervalRef.current);
      if (speechRecRef.current) { try { speechRecRef.current.stop(); } catch {} }

      setIsRecording(false);

      if (streamRecordId) {
        toast.info('Arrêt de l\'enregistrement... Transcription en cours');
        const res = await fetch(`${API_URL}/api/visio/stream/${streamRecordId}/stop`, {
          method: 'POST', headers: jsonHeaders
        });
        const data = await res.json();
        if (data.success) {
          toast.success('Transcription et analyse IA lancées !');
          setActiveTab('records');
          loadRecords();
          // Polling pour l'analyse
          pollRecordStatus(streamRecordId);
        }
      }
      setStreamRecordId(null);
    } catch (err) {
      console.error('Erreur arrêt:', err);
      setIsRecording(false);
    }
  };

  const pollRecordStatus = async (recordId, attempts = 0) => {
    if (attempts >= 30) return; // max 5 min
    try {
      const res = await fetch(`${API_URL}/api/visio/records/${recordId}`, { headers });
      const data = await res.json();
      if (data.record?.analyse?.status === 'completed') {
        toast.success('Analyse terminée ! Consultez les actions proposées.');
        loadRecords();
        return;
      }
      setTimeout(() => pollRecordStatus(recordId, attempts + 1), 10000);
    } catch (e) {
      if (attempts < 29) setTimeout(() => pollRecordStatus(recordId, attempts + 1), 10000);
    }
  };

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  // === ACTIONS POST-VISIO ===
  const openActionsDialog = (record) => {
    setActionRecord(record);
    const analyse = record.analyse || {};
    const email = analyse.brouillon_email || {};
    const rdv = analyse.prochain_rdv || {};
    setEditEmail({
      destinataire: email.destinataire || '',
      objet: email.objet || 'Suite à notre échange - Bureau Agricole',
      corps: email.corps || ''
    });
    setEditMeet({
      date: rdv.date || '',
      heure: rdv.heure || '14:00',
      objet: rdv.objet || 'Suite à notre échange - Bureau Agricole',
      invites: email.destinataire || '',
      duree_minutes: 30
    });
    setShowActionsDialog(true);
  };

  const envoyerEmail = async () => {
    if (!actionRecord) return;
    setSendingAction('email');
    try {
      const res = await fetch(`${API_URL}/api/visio/records/${actionRecord.id}/envoyer-email`, {
        method: 'POST', headers: jsonHeaders,
        body: JSON.stringify(editEmail)
      });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message);
      } else throw new Error(data.detail);
    } catch (e) {
      toast.error(e.message || 'Erreur envoi email');
    } finally { setSendingAction(null); }
  };

  const creerInvitationMeet = async () => {
    if (!actionRecord) return;
    setSendingAction('meet');
    try {
      const res = await fetch(`${API_URL}/api/visio/records/${actionRecord.id}/creer-invitation-meet`, {
        method: 'POST', headers: jsonHeaders,
        body: JSON.stringify({
          ...editMeet,
          invites: editMeet.invites.split(',').map(e => e.trim()).filter(Boolean)
        })
      });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message);
        if (data.meet_link) toast.info(`Lien Meet: ${data.meet_link}`);
      } else throw new Error(data.detail);
    } catch (e) {
      toast.error(e.message || 'Erreur création Meet');
    } finally { setSendingAction(null); }
  };

  // Charger les invitations
  const loadInvitations = useCallback(async () => {
    setLoadingInvitations(true);
    try {
      const res = await fetch(`${API_URL}/api/visio/invitations?days_back=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      
      if (data.success) {
        setInvitations(data.invitations || []);
        setGoogleConnected(true);
      } else if (data.error === 'google_not_connected') {
        setGoogleConnected(false);
        setInvitations([]);
      }
    } catch (error) {
      console.error('Erreur chargement invitations:', error);
    } finally {
      setLoadingInvitations(false);
    }
  }, [token]);

  // Charger les enregistrements
  const loadRecords = useCallback(async () => {
    setLoadingRecords(true);
    try {
      const res = await fetch(`${API_URL}/api/visio/records`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setRecords(data.records || []);
    } catch (error) {
      console.error('Erreur chargement enregistrements:', error);
    } finally {
      setLoadingRecords(false);
    }
  }, [token]);

  useEffect(() => {
    loadInvitations();
    loadRecords();
  }, [loadInvitations, loadRecords]);

  // Upload d'un enregistrement
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Vérifier le type
    if (!file.type.startsWith('audio/') && !file.type.startsWith('video/')) {
      toast.error('Format non supporté', {
        description: 'Utilisez un fichier audio ou vidéo (mp4, mp3, webm, etc.)'
      });
      return;
    }

    // Vérifier la taille (500MB max)
    if (file.size > 500 * 1024 * 1024) {
      toast.error('Fichier trop volumineux', {
        description: 'Maximum 500 MB'
      });
      return;
    }

    setUploading(true);
    setUploadProgress(10);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('titre', uploadTitle || file.name.replace(/\.[^/.]+$/, ''));

      setUploadProgress(30);

      const res = await fetch(`${API_URL}/api/visio/analyze`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      setUploadProgress(80);

      const data = await res.json();

      if (data.success) {
        toast.success('Fichier envoyé !', {
          description: 'Transcription et analyse en cours...'
        });
        loadRecords();
        setUploadTitle('');
        setActiveTab('records');
      } else {
        throw new Error(data.detail || 'Erreur upload');
      }
    } catch (error) {
      console.error('Erreur upload:', error);
      toast.error('Erreur d\'upload', {
        description: error.message
      });
    } finally {
      setUploading(false);
      setUploadProgress(0);
      e.target.value = '';
    }
  };

  // Supprimer un enregistrement
  const deleteRecord = async (recordId) => {
    if (!confirm('Supprimer cet enregistrement ?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/visio/records/${recordId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Enregistrement supprimé');
        loadRecords();
      }
    } catch (error) {
      toast.error('Erreur suppression');
    }
  };

  // Relancer l'analyse
  const reanalyzeRecord = async (recordId) => {
    try {
      toast.info('Réanalyse en cours...');
      
      const res = await fetch(`${API_URL}/api/visio/records/${recordId}/reanalyze`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success('Réanalyse lancée');
        loadRecords();
      }
    } catch (error) {
      toast.error('Erreur réanalyse');
    }
  };

  // Voir le détail d'un enregistrement
  const viewRecord = async (recordId) => {
    try {
      const res = await fetch(`${API_URL}/api/visio/records/${recordId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      
      if (data.success) {
        setSelectedRecord(data.record);
      }
    } catch (error) {
      toast.error('Erreur chargement');
    }
  };

  // Rendu du statut
  const renderStatus = (status) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-500"><CheckCircle2 className="w-3 h-3 mr-1" /> Terminé</Badge>;
      case 'processing':
        return <Badge variant="secondary"><Loader2 className="w-3 h-3 mr-1 animate-spin" /> En cours</Badge>;
      case 'failed':
        return <Badge variant="destructive"><AlertCircle className="w-3 h-3 mr-1" /> Échec</Badge>;
      default:
        return <Badge variant="outline">En attente</Badge>;
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Video className="w-7 h-7 text-primary" />
            Assistant Visio
          </h1>
          <p className="text-muted-foreground">
            Détectez vos invitations de visio et analysez vos enregistrements
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 max-w-md">
          <TabsTrigger value="invitations" className="gap-2">
            <Mail className="w-4 h-4" />
            Invitations
          </TabsTrigger>
          <TabsTrigger value="upload" className="gap-2">
            <Upload className="w-4 h-4" />
            Analyser
          </TabsTrigger>
          <TabsTrigger value="records" className="gap-2">
            <FileText className="w-4 h-4" />
            Analyses
          </TabsTrigger>
        </TabsList>

        {/* Tab Invitations */}
        <TabsContent value="invitations" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Invitations de visioconférence
                  </CardTitle>
                  <CardDescription>
                    Détectées automatiquement depuis vos emails
                  </CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={loadInvitations}
                  disabled={loadingInvitations}
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${loadingInvitations ? 'animate-spin' : ''}`} />
                  Actualiser
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {!googleConnected ? (
                <div className="text-center py-12 space-y-4">
                  <Mail className="w-16 h-16 mx-auto text-muted-foreground/30" />
                  <div>
                    <p className="font-medium">Compte Google non connecté</p>
                    <p className="text-sm text-muted-foreground">
                      Connectez votre compte Google pour détecter automatiquement vos invitations
                    </p>
                  </div>
                  <Button onClick={() => window.location.href = '/integrations'}>
                    Connecter Google
                  </Button>
                </div>
              ) : loadingInvitations ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                </div>
              ) : invitations.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Calendar className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>Aucune invitation de visio détectée</p>
                  <p className="text-sm">Les emails avec liens Zoom, Meet ou Teams apparaîtront ici</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {invitations.map((inv) => (
                    <div
                      key={inv.id}
                      className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${
                        inv.is_future ? 'bg-card hover:bg-muted/50' : 'bg-muted/30'
                      }`}
                      data-testid={`invitation-${inv.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <PlatformIcon platform={inv.plateforme} />
                        
                        <div>
                          <h3 className="font-medium line-clamp-1">{inv.sujet}</h3>
                          <div className="flex items-center gap-3 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {inv.organisateur}
                            </span>
                            {inv.date_reunion && (
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {inv.date_reunion}
                                {inv.heure && ` à ${inv.heure}`}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Badge variant={inv.is_future ? "default" : "secondary"}>
                          {inv.plateforme.charAt(0).toUpperCase() + inv.plateforme.slice(1)}
                        </Badge>
                        {inv.lien_visio && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => window.open(inv.lien_visio, '_blank')}
                          >
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Upload / Enregistrement */}
        <TabsContent value="upload" className="space-y-4">
          {/* ENREGISTREMENT STREAMING + COACH IA */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className={`lg:col-span-2 border-2 ${isRecording ? 'border-red-400 bg-red-50/50' : 'border-primary/30 bg-gradient-to-br from-primary/5 to-transparent'}`}>
              <CardContent className="p-8">
                <div className="flex flex-col items-center text-center space-y-6">
                  <div className={`p-4 rounded-full ${isRecording ? 'bg-red-100 animate-pulse' : 'bg-primary/10'}`}>
                    {isRecording ? <Mic className="w-12 h-12 text-red-600" /> : <Mic className="w-12 h-12 text-primary" />}
                  </div>
                  
                  <div>
                    <h2 className="text-xl font-semibold mb-2">
                      {isRecording ? `Enregistrement en cours... ${formatTime(recordingTime)}` : 'Enregistrer une visio'}
                    </h2>
                    <p className="text-muted-foreground max-w-md">
                      {isRecording 
                        ? 'Le coach IA analyse en temps réel et vous suggère des actions de closing.'
                        : 'Lancez l\'enregistrement. Le coach IA vous aidera à closer en temps réel.'}
                    </p>
                  </div>

                  <div className="flex gap-3">
                    {!isRecording ? (
                      <Button 
                        size="lg" 
                        className="bg-red-600 hover:bg-red-700 gap-2"
                        onClick={startRecording}
                        data-testid="btn-start-recording"
                      >
                        <Mic className="w-5 h-5" />
                        Démarrer l'enregistrement + Coach IA
                      </Button>
                    ) : (
                      <Button 
                        size="lg" 
                        variant="destructive"
                        className="gap-2"
                        onClick={stopRecording}
                        data-testid="btn-stop-recording"
                      >
                        <Square className="w-5 h-5" />
                        Arrêter ({formatTime(recordingTime)})
                      </Button>
                    )}
                  </div>

                  {isRecording && (
                    <>
                      <div className="flex items-center gap-2 text-red-600 text-sm">
                        <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
                        Streaming actif - {recordingTime > 0 ? `${Math.floor(recordingTime / 5)} chunks` : 'En attente...'}
                      </div>
                      {liveTranscript && (
                        <div className="w-full text-left p-3 bg-white/80 rounded-lg border max-h-32 overflow-y-auto">
                          <p className="text-xs text-gray-500 mb-1 font-medium">Transcription live</p>
                          <p className="text-sm text-gray-700">{liveTranscript.slice(-300)}</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* COACH IA SIDEBAR - TOUJOURS VISIBLE */}
            <Card className={`border-2 ${isRecording ? 'border-amber-300' : 'border-gray-200'} bg-gradient-to-b from-amber-50 to-white`} data-testid="coaching-sidebar">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Sparkles className={`w-4 h-4 ${isRecording ? 'text-amber-600 animate-pulse' : 'text-amber-400'}`} />
                  Coach Closing IA
                </CardTitle>
                <CardDescription className="text-xs">
                  {isRecording ? 'Valentine analyse en temps réel...' : 'Lance l\'enregistrement pour activer le coaching'}
                </CardDescription>
              </CardHeader>
              <CardContent className="p-3">
                <ScrollArea className="h-[350px]">
                  {!isRecording ? (
                    <div className="text-center py-8 text-muted-foreground space-y-3">
                      <Brain className="w-10 h-10 mx-auto opacity-30" />
                      <p className="text-xs font-medium">Coach IA en attente</p>
                      <p className="text-[10px]">Pendant l'appel, Valentine te suggèrera :</p>
                      <div className="space-y-1.5 text-left px-2">
                        {[
                          { color: 'bg-green-100 text-green-700', label: 'Signaux d\'achat à exploiter' },
                          { color: 'bg-red-100 text-red-700', label: 'Réponses aux objections' },
                          { color: 'bg-amber-100 text-amber-700', label: 'Bon moment pour closer' },
                          { color: 'bg-purple-100 text-purple-700', label: 'Questions clés à poser' },
                          { color: 'bg-blue-100 text-blue-700', label: 'Arguments de vente' },
                        ].map((item, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <Badge className={`text-[9px] ${item.color}`}>{item.label}</Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : coachingTips.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Brain className="w-8 h-8 mx-auto mb-2 opacity-40 animate-pulse" />
                      <p className="text-xs">Valentine écoute...</p>
                      <p className="text-[10px] mt-1">Les tips apparaîtront pendant la conversation</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {coachingTips.map((tip, i) => (
                        <div
                          key={tip.id || i}
                          className={`p-2.5 rounded-lg border text-sm transition-all ${
                            tip.urgence === 'haute'
                              ? 'bg-red-50 border-red-200 animate-pulse'
                              : 'bg-white border-gray-200'
                          }`}
                          data-testid={`coaching-tip-${i}`}
                        >
                          <div className="flex items-start gap-2">
                            <Badge className={`text-[9px] shrink-0 ${
                              tip.type === 'signal_achat' ? 'bg-green-100 text-green-700' :
                              tip.type === 'objection' ? 'bg-red-100 text-red-700' :
                              tip.type === 'timing' ? 'bg-amber-100 text-amber-700' :
                              tip.type === 'question_cle' ? 'bg-purple-100 text-purple-700' :
                              'bg-blue-100 text-blue-700'
                            }`}>
                              {tip.type === 'signal_achat' ? 'Signal' :
                               tip.type === 'objection' ? 'Objection' :
                               tip.type === 'timing' ? 'Timing' :
                               tip.type === 'question_cle' ? 'Question' :
                               tip.type === 'argument' ? 'Argument' :
                               tip.type}
                            </Badge>
                            <p className="text-xs leading-relaxed flex-1">{tip.texte}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          {/* OU UPLOAD FICHIER */}
          <Card className="border border-dashed">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <Upload className="w-8 h-8 text-muted-foreground" />
                <div className="flex-1">
                  <h3 className="font-medium">Ou uploadez un fichier audio/vidéo</h3>
                  <p className="text-sm text-muted-foreground">MP4, WebM, MP3, WAV (max 500 MB)</p>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    placeholder="Titre (optionnel)"
                    value={uploadTitle}
                    onChange={(e) => setUploadTitle(e.target.value)}
                    disabled={uploading}
                    className="w-48"
                    data-testid="upload-title-input"
                  />
                  <Button
                    disabled={uploading}
                    onClick={() => document.getElementById('visio-file-upload').click()}
                    data-testid="btn-upload"
                  >
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  </Button>
                </div>
                <input type="file" id="visio-file-upload" className="hidden" accept="video/*,audio/*" onChange={handleFileUpload} />
              </div>
            </CardContent>
          </Card>

          {/* Ce que l'IA détecte */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4 flex items-start gap-3">
                <div className="p-2 bg-blue-100 text-blue-600 rounded-lg"><FileText className="w-5 h-5" /></div>
                <div>
                  <h3 className="font-medium">Transcription</h3>
                  <p className="text-xs text-muted-foreground">Audio en texte automatique</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-start gap-3">
                <div className="p-2 bg-purple-100 text-purple-600 rounded-lg"><Brain className="w-5 h-5" /></div>
                <div>
                  <h3 className="font-medium">Analyse commerciale</h3>
                  <p className="text-xs text-muted-foreground">Signaux de closing, ambiance</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-start gap-3">
                <div className="p-2 bg-green-100 text-green-600 rounded-lg"><Send className="w-5 h-5" /></div>
                <div>
                  <h3 className="font-medium">Email de suivi</h3>
                  <p className="text-xs text-muted-foreground">Brouillon pré-rédigé par l'IA</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-start gap-3">
                <div className="p-2 bg-amber-100 text-amber-600 rounded-lg"><CalendarPlus className="w-5 h-5" /></div>
                <div>
                  <h3 className="font-medium">Prochain RDV</h3>
                  <p className="text-xs text-muted-foreground">Invitation Meet automatique</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tab Records */}
        <TabsContent value="records" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Mes analyses
                  </CardTitle>
                  <CardDescription>
                    {records.length} enregistrement{records.length > 1 ? 's' : ''} analysé{records.length > 1 ? 's' : ''}
                  </CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={loadRecords}
                  disabled={loadingRecords}
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${loadingRecords ? 'animate-spin' : ''}`} />
                  Actualiser
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingRecords ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                </div>
              ) : records.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Video className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>Aucun enregistrement analysé</p>
                  <p className="text-sm">Uploadez votre premier enregistrement pour commencer</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {records.map((record) => (
                    <div
                      key={record.id}
                      className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                      data-testid={`record-${record.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="p-2 bg-primary/10 rounded-full">
                          <Video className="w-5 h-5 text-primary" />
                        </div>
                        
                        <div>
                          <h3 className="font-medium">{record.titre}</h3>
                          <div className="flex items-center gap-3 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {new Date(record.created_at).toLocaleDateString('fr-FR', {
                                day: 'numeric',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                            {record.fichier?.size_mb && (
                              <span>{record.fichier.size_mb.toFixed(1)} MB</span>
                            )}
                            {record.transcription?.words_count > 0 && (
                              <span>{record.transcription.words_count} mots</span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <div className="flex flex-col items-end gap-1">
                          {renderStatus(record.transcription?.status)}
                          {record.analyse?.status === 'completed' && (
                            <Badge variant="outline" className="text-xs">
                              <Brain className="w-3 h-3 mr-1" />
                              Analysé
                            </Badge>
                          )}
                          {record.analyse?.score_closing?.score != null && (
                            <Badge className={`text-xs ${
                              record.analyse.score_closing.score >= 7 ? 'bg-green-100 text-green-800' :
                              record.analyse.score_closing.score >= 4 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-700'
                            }`}>
                              {record.analyse.score_closing.score}/10
                            </Badge>
                          )}
                        </div>
                        
                        <div className="flex gap-1">
                          {record.transcription?.status === 'completed' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => viewRecord(record.id)}
                              title="Voir l'analyse"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          )}
                          
                          {record.transcription?.status === 'completed' && record.analyse?.status !== 'processing' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => reanalyzeRecord(record.id)}
                              title="Réanalyser"
                            >
                              <RefreshCw className="w-4 h-4" />
                            </Button>
                          )}

                          {record.analyse?.status === 'completed' && (
                            <Button
                              size="sm"
                              variant="default"
                              className="bg-amber-600 hover:bg-amber-700 gap-1"
                              onClick={() => openActionsDialog(record)}
                              title="Actions post-visio"
                              data-testid={`btn-actions-${record.id}`}
                            >
                              <Sparkles className="w-4 h-4" />
                              Actions
                            </Button>
                          )}
                          
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteRecord(record.id)}
                            title="Supprimer"
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal détail analyse */}
      {selectedRecord && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <Card className="w-full max-w-3xl max-h-[85vh] overflow-hidden">
            <CardHeader className="border-b">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>{selectedRecord.titre}</CardTitle>
                  <CardDescription>
                    {selectedRecord.transcription?.words_count} mots transcrits
                  </CardDescription>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setSelectedRecord(null)}>
                  Fermer
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Tabs defaultValue="analyse">
                <TabsList className="w-full rounded-none border-b">
                  <TabsTrigger value="analyse">Analyse IA</TabsTrigger>
                  <TabsTrigger value="transcription">Transcription</TabsTrigger>
                </TabsList>
                
                <TabsContent value="analyse" className="m-0">
                  <ScrollArea className="h-[55vh] p-4">
                    {selectedRecord.analyse?.status === 'completed' ? (
                      <div className="space-y-6">
                        {/* Résumé */}
                        <div>
                          <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
                            <FileText className="w-5 h-5 text-blue-500" />
                            Résumé
                          </h3>
                          <p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">
                            {selectedRecord.analyse.resume || "Aucun résumé disponible"}
                          </p>
                        </div>

                        {/* Points clés */}
                        {selectedRecord.analyse.points_cles?.length > 0 && (
                          <div>
                            <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
                              <CheckCircle2 className="w-5 h-5 text-green-500" />
                              Points clés
                            </h3>
                            <ul className="space-y-2">
                              {selectedRecord.analyse.points_cles.map((point, i) => (
                                <li key={i} className="text-sm flex items-start gap-2 p-2 bg-muted/30 rounded">
                                  <span className="text-green-500 font-bold">•</span>
                                  {point}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Décisions */}
                        {selectedRecord.analyse.decisions?.length > 0 && (
                          <div>
                            <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
                              <Brain className="w-5 h-5 text-purple-500" />
                              Décisions prises
                            </h3>
                            <ul className="space-y-2">
                              {selectedRecord.analyse.decisions.map((decision, i) => (
                                <li key={i} className="text-sm flex items-start gap-2 p-2 bg-muted/30 rounded">
                                  <span className="text-purple-500 font-bold">→</span>
                                  {decision}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Actions */}
                        {selectedRecord.analyse.actions?.length > 0 && (
                          <div>
                            <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
                              <ListTodo className="w-5 h-5 text-orange-500" />
                              Actions à suivre
                            </h3>
                            <div className="space-y-2">
                              {selectedRecord.analyse.actions.map((action, i) => (
                                <div key={i} className="p-3 rounded-lg bg-muted/50 border">
                                  <p className="text-sm font-medium">{action.action}</p>
                                  <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                                    {action.responsable && (
                                      <span className="flex items-center gap-1">
                                        <Users className="w-3 h-3" />
                                        {action.responsable}
                                      </span>
                                    )}
                                    {action.deadline && (
                                      <span className="flex items-center gap-1">
                                        <Calendar className="w-3 h-3" />
                                        {action.deadline}
                                      </span>
                                    )}
                                    {action.priorite && (
                                      <Badge variant={
                                        action.priorite === 'haute' ? 'destructive' : 
                                        action.priorite === 'moyenne' ? 'secondary' : 'outline'
                                      } className="text-xs">
                                        {action.priorite}
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : selectedRecord.analyse?.status === 'processing' ? (
                      <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
                        <Loader2 className="w-8 h-8 animate-spin mb-4" />
                        <p>Analyse en cours...</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
                        <AlertCircle className="w-8 h-8 mb-4" />
                        <p>Analyse non disponible</p>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="mt-4"
                          onClick={() => reanalyzeRecord(selectedRecord.id)}
                        >
                          Lancer l'analyse
                        </Button>
                      </div>
                    )}
                  </ScrollArea>
                </TabsContent>
                
                <TabsContent value="transcription" className="m-0">
                  <ScrollArea className="h-[55vh] p-4">
                    <p className="whitespace-pre-wrap text-sm leading-relaxed">
                      {selectedRecord.transcription?.text || 'Transcription non disponible'}
                    </p>
                  </ScrollArea>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      )}

      {/* === DIALOG ACTIONS POST-VISIO === */}
      <Dialog open={showActionsDialog} onOpenChange={setShowActionsDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-600" />
              Actions post-visio proposées par l'IA
            </DialogTitle>
          </DialogHeader>

          {actionRecord?.analyse && (
            <div className="space-y-6">
              {/* Résumé */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h3 className="font-semibold text-blue-800 mb-1">Résumé</h3>
                <p className="text-sm text-blue-700">{actionRecord.analyse.resume}</p>
                {actionRecord.analyse.ambiance && (
                  <Badge className="mt-2" variant="outline">
                    Ambiance : {actionRecord.analyse.ambiance}
                  </Badge>
                )}
                {actionRecord.analyse.signal_closing && (
                  <Badge className="mt-2 ml-2 bg-green-100 text-green-800">
                    <ThumbsUp className="w-3 h-3 mr-1" /> Signal de closing détecté
                  </Badge>
                )}
                {actionRecord.analyse.score_closing && (
                  <div className="mt-3 flex items-center gap-3">
                    <div className={`text-2xl font-bold ${
                      actionRecord.analyse.score_closing.score >= 7 ? 'text-green-600' :
                      actionRecord.analyse.score_closing.score >= 4 ? 'text-amber-600' : 'text-red-500'
                    }`}>
                      {actionRecord.analyse.score_closing.score}/10
                    </div>
                    <div>
                      <div className="text-xs font-medium text-gray-500">Probabilité de closing</div>
                      <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${
                            actionRecord.analyse.score_closing.score >= 7 ? 'bg-green-500' :
                            actionRecord.analyse.score_closing.score >= 4 ? 'bg-amber-500' : 'bg-red-400'
                          }`}
                          style={{ width: `${actionRecord.analyse.score_closing.score * 10}%` }}
                        />
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">{actionRecord.analyse.score_closing.justification}</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Actions détectées */}
              {actionRecord.analyse.actions?.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-2 flex items-center gap-2">
                    <ListTodo className="w-4 h-4" /> Actions à suivre
                  </h3>
                  <div className="space-y-2">
                    {actionRecord.analyse.actions.map((a, i) => (
                      <div key={i} className="flex items-start gap-2 p-2 bg-gray-50 rounded text-sm">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="font-medium">{a.action}</span>
                          {a.responsable && <span className="text-muted-foreground ml-2">({a.responsable})</span>}
                          {a.deadline && <span className="text-muted-foreground ml-2">- {a.deadline}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Email de suivi */}
              {actionRecord.analyse.brouillon_email?.necessaire !== false && (
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <h3 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
                    <Mail className="w-4 h-4" /> Email de suivi
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs">Destinataire</Label>
                      <Input
                        value={editEmail.destinataire}
                        onChange={e => setEditEmail(prev => ({ ...prev, destinataire: e.target.value }))}
                        placeholder="email@exemple.com"
                        data-testid="action-email-dest"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Objet</Label>
                      <Input
                        value={editEmail.objet}
                        onChange={e => setEditEmail(prev => ({ ...prev, objet: e.target.value }))}
                        data-testid="action-email-objet"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Corps du mail</Label>
                      <Textarea
                        value={editEmail.corps}
                        onChange={e => setEditEmail(prev => ({ ...prev, corps: e.target.value }))}
                        rows={5}
                        data-testid="action-email-corps"
                      />
                    </div>
                    <Button
                      className="bg-green-600 hover:bg-green-700 gap-2"
                      onClick={envoyerEmail}
                      disabled={sendingAction === 'email' || !editEmail.destinataire}
                      data-testid="btn-send-email"
                    >
                      {sendingAction === 'email' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      Envoyer l'email
                    </Button>
                  </div>
                </div>
              )}

              {/* Prochain RDV - Invitation Meet */}
              {actionRecord.analyse.prochain_rdv?.detecte && (
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <h3 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
                    <CalendarPlus className="w-4 h-4" /> Prochain rendez-vous détecté
                  </h3>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Date</Label>
                        <Input
                          type="date"
                          value={editMeet.date}
                          onChange={e => setEditMeet(prev => ({ ...prev, date: e.target.value }))}
                          data-testid="action-meet-date"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Heure</Label>
                        <Input
                          type="time"
                          value={editMeet.heure}
                          onChange={e => setEditMeet(prev => ({ ...prev, heure: e.target.value }))}
                          data-testid="action-meet-heure"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Objet</Label>
                      <Input
                        value={editMeet.objet}
                        onChange={e => setEditMeet(prev => ({ ...prev, objet: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Invités (emails séparés par des virgules)</Label>
                      <Input
                        value={editMeet.invites}
                        onChange={e => setEditMeet(prev => ({ ...prev, invites: e.target.value }))}
                        placeholder="prospect@email.com, collegue@email.com"
                        data-testid="action-meet-invites"
                      />
                    </div>
                    <Button
                      className="bg-amber-600 hover:bg-amber-700 gap-2"
                      onClick={creerInvitationMeet}
                      disabled={sendingAction === 'meet' || !editMeet.date}
                      data-testid="btn-create-meet"
                    >
                      {sendingAction === 'meet' ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarPlus className="w-4 h-4" />}
                      Créer l'invitation Google Meet
                    </Button>
                  </div>
                </div>
              )}

              {/* Proposition devis */}
              {actionRecord.analyse.proposition_devis?.necessaire && (
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <h3 className="font-semibold text-purple-800 mb-2 flex items-center gap-2">
                    <FileSignature className="w-4 h-4" /> Proposition de devis détectée
                  </h3>
                  <div className="text-sm space-y-1">
                    <p><strong>Client :</strong> {actionRecord.analyse.proposition_devis.client}</p>
                    <p><strong>Palier :</strong> {actionRecord.analyse.proposition_devis.palier}</p>
                    {actionRecord.analyse.proposition_devis.prix_mentionne > 0 && (
                      <p><strong>Prix mentionné :</strong> {actionRecord.analyse.proposition_devis.prix_mentionne}€/mois</p>
                    )}
                    <p className="text-muted-foreground">{actionRecord.analyse.proposition_devis.details}</p>
                  </div>
                  <Button variant="outline" className="mt-3 gap-2" size="sm">
                    <FileSignature className="w-4 h-4" /> Générer le devis dans le CRM
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
